import axios from '../axiosConfig'
import type { ProjectBranch, ProjectUser, ProjectComparison } from './projectService'
import type { AppendixConsistencyReport, UnresolvedAppendixItem, UnselectableValuesReport } from '../types/codeIndex'

/**
 * The unselectable-values report carried by a 409 from opening a pull request, or null when the
 * error is anything else (including a merge-conflict 409, whose body has no such report).
 */
export function extractUnselectableValuesReport(error: unknown): UnselectableValuesReport | null {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response
  if (!response || response.status !== 409) return null

  const report = (response.data as { unselectableValues?: UnselectableValuesReport } | null)?.unselectableValues
  if (!report || !Array.isArray(report.values)) return null

  return report
}

/**
 * The appendices that block opening a pull request because some of their entries point at code
 * items or values that no longer exist (a 409 like the unselectable-values one), or null.
 */
export function extractUnresolvedAppendices(error: unknown): UnresolvedAppendixItem[] | null {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response
  if (!response || response.status !== 409) return null

  const items = (response.data as { unresolvedAppendices?: unknown } | null)?.unresolvedAppendices
  if (!Array.isArray(items)) return null

  return items
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map(item => ({
      appendixKey: String(item.appendixKey ?? ''),
      appendixId: typeof item.appendixId === 'string' && item.appendixId ? item.appendixId : null,
      title: String(item.title ?? ''),
      letter: typeof item.letter === 'string' && item.letter ? item.letter : null,
      unresolvedCount: Number(item.unresolvedCount ?? 0),
    }))
}

/**
 * The rules-versus-appendix report carried by a 409 from opening a pull request, or null when the
 * error is anything else or the submit was refused for unselectable values only.
 */
export function extractAppendixConsistencyReport(error: unknown): AppendixConsistencyReport | null {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response
  if (!response || response.status !== 409) return null

  const report = (response.data as { appendixConsistency?: AppendixConsistencyReport } | null)?.appendixConsistency
  if (!report || !Array.isArray(report.appendices)) return null

  return report
}

export type PullRequestState = 'Draft' | 'Open' | 'Approved' | 'Merged' | 'Closed' | string
export type ReviewDecision = 'Pending' | 'Approved' | 'Rejected'

export interface CreatePullRequestRequest {
  sourceProjectId: string
  targetProjectId?: string | null
  title: string
  description?: string | null
}

export interface PullRequestChangeReviewItem {
  changeId: string
  decision: Exclude<ReviewDecision, 'Pending'>
  comment?: string | null
}

export interface ReviewPullRequestChangesRequest {
  reviews: PullRequestChangeReviewItem[]
}

export interface PullRequestChange {
  id: string
  changeType: number | string
  nodeType: string
  nodeId?: string | null
  // Every node covered by a rolled-up change (an appendix with its tables, columns, rows and cells).
  nodeIds?: string[] | null
  parentNodeId?: string | null
  relationshipType?: string | null
  path?: string | null
  oldValue?: string | null
  newValue?: string | null
  approvalStatus?: number | string | null
  approvalComment?: string | null
  reviewedById?: string | null
  reviewedAt?: string | null
  withdrawnAt?: string | null
  canRevise?: boolean
  createdBy?: ProjectUser | null
  createdAt?: string | null
  updatedAt?: string | null
}

export async function resubmitPullRequestChange(
  codeIndexId: string,
  pullRequestId: string,
  changeId: string
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests/${encodeURIComponent(pullRequestId)}/changes/${encodeURIComponent(changeId)}/resubmit`,
    null
  )
  return data
}

export async function withdrawPullRequestChange(
  codeIndexId: string,
  pullRequestId: string,
  changeId: string
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests/${encodeURIComponent(pullRequestId)}/changes/${encodeURIComponent(changeId)}/withdraw`,
    null
  )
  return data
}

export interface ResolvePullRequestConflictRequest {
  resolution: 'UseMain' | 'KeepProject'
  expectedLiveValue?: string | null
  expectedProjectValue?: string | null
}

export async function resolvePullRequestConflict(
  codeIndexId: string, pullRequestId: string, changeId: string, request: ResolvePullRequestConflictRequest
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests/${encodeURIComponent(pullRequestId)}/changes/${encodeURIComponent(changeId)}/resolve`,
    request
  )
  return data
}

export async function restoreDeletedPullRequestChange(
  codeIndexId: string,
  pullRequestId: string,
  changeId: string
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests/${encodeURIComponent(pullRequestId)}/changes/${encodeURIComponent(changeId)}/restore`,
    null
  )
  return data
}

export interface PullRequestChangeSet {
  id: string
  title?: string | null
  description?: string | null
  status?: number | string | null
  isMajor?: boolean
  approvalComment?: string | null
  reviewedById?: string | null
  reviewedAt?: string | null
  changes?: PullRequestChange[]
  reviews?: PullRequestReview[]
  createdBy?: ProjectUser | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface PullRequestReview {
  id?: string | null
  decision?: number | string | null
  comment?: string | null
  reviewer?: ProjectUser | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface PullRequestDTO {
  id?: string | null
  title: string
  description?: string | null
  state?: PullRequestState | null
  targetKind?: string | null
  merged?: string | null
  sourceBranch?: ProjectBranch | null
  targetBranch?: ProjectBranch | null
  conflicts?: ProjectComparison['conflicts'] | null
  changeset?: PullRequestChangeSet | null
  reviews?: unknown[]
  createdBy?: ProjectUser | null
  message?: string | null
}

export async function fetchPullRequests(codeIndexId: string, projectId?: string | null): Promise<PullRequestDTO[]> {
  const url = `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests`
  const { data } = projectId
    ? await axios.get<PullRequestDTO[]>(url, { params: { projectId } })
    : await axios.get<PullRequestDTO[]>(url)
  return Array.isArray(data) ? data : []
}

export async function createPullRequest(
  codeIndexId: string,
  payload: CreatePullRequestRequest
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests`,
    payload,
    { headers: { 'Content-Type': 'application/json' } }
  )
  return data
}

export async function reviewPullRequestChanges(
  codeIndexId: string,
  pullRequestId: string,
  payload: ReviewPullRequestChangesRequest
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests/${encodeURIComponent(pullRequestId)}/changes/review`,
    payload,
    { headers: { 'Content-Type': 'application/json' } }
  )
  return data
}

export async function mergePullRequest(
  codeIndexId: string,
  pullRequestId: string
): Promise<PullRequestDTO> {
  const { data } = await axios.post<PullRequestDTO>(
    `/codeindex/${encodeURIComponent(codeIndexId)}/pull-requests/${encodeURIComponent(pullRequestId)}/merge`,
    null,
    { headers: { 'Content-Type': 'application/json' } }
  )
  return data
}
